import { toast } from "react-toastify";

/**
 * Check environment before console logging data
 * @param  {...any} data
 * @param {string} environment
 * @returns {boolean}
 */
const handlerDump = (...data) => {
  if (process.env.REACT_APP_APP_ENV === "development") {
    console.log(...data);
    return true;
  }
  return false;
};

/**
 * Determine response of handler
 * @param {string} alternateMessage
 * @param {object} data
 * @returns {string}
 */
const handlerResponse = (alternateMessage, data = {}) => {
  if (data.data && data.data.message) {
    return data.data.message;
  }
  return alternateMessage;
};

/**
 * Check if error should be displayed based on environment and status code
 * @param {number} statusCode
 * @returns {boolean}
 */
const shouldDisplayError = (statusCode) => {
  const isDevelopment = process.env.REACT_APP_APP_ENV === "development";

  // In development, show all errors
  if (isDevelopment) {
    return true;
  }

  // In production, only show 400 and 422 errors
  return statusCode === 400 || statusCode === 422;
};

/**
 * Parse error response from server
 * @param {*} errorObject
 * @param {boolean} shouldDispatchAlert
 * @returns {string}
 */
const handler = (errorObject, shouldDispatchAlert = false) => {
  try {
    if (errorObject.response && errorObject.response.data) {
      let errors = errorObject?.response?.data?.errors;
      let message = errorObject?.response?.data?.message;
      let statusCode = errorObject?.response?.status;

      // Check if we should display this error based on environment and status code
      const canDisplayError = shouldDisplayError(statusCode);

      if (errors) {
        // Request made and server responded with an error
        handlerDump(
          "Error Response",
          errorObject?.response?.headers,
          errorObject?.response?.data,
          errorObject?.response?.status
        );

        // If error should not be displayed, log to console instead
        if (!canDisplayError) {
          console.error("Error Response:", {
            status: statusCode,
            errors: errors,
            headers: errorObject?.response?.headers,
            data: errorObject?.response?.data,
          });
        }

        // For 422 errors, display only the first error
        if (statusCode === 422) {
          const firstError = Object.values(errors)[0];
          if (shouldDispatchAlert && canDisplayError && firstError) {
            toast.warning(Array.isArray(firstError) ? firstError[0] : firstError.toString(), {
              position: toast.POSITION.TOP_RIGHT,
              theme: "colored",
              autoClose: 6000,
            });
          }
        } else {
          // For other errors, map through all errors
          Object.values(errors).map((item, key) => {
            // Send found errors to alert
            return (
              shouldDispatchAlert &&
              canDisplayError &&
              toast.warning(item.toString(), {
                position: toast.POSITION.TOP_RIGHT,
                theme: "colored",
                autoClose: 6000,
              })
            );
          });
        }
      } else if (message && typeof message === "object") {
        // Request made and server responded with an error
        handlerDump(
          "Error Response",
          errorObject?.response?.headers,
          errorObject?.response?.data,
          errorObject?.response?.status
        );

        // If error should not be displayed, log to console instead
        if (!canDisplayError) {
          console.error("Error Response:", {
            status: statusCode,
            message: message,
            headers: errorObject?.response?.headers,
            data: errorObject?.response?.data,
          });
        }

        // For 422 errors, display only the first message
        if (statusCode === 422) {
          const firstMessage = Object.values(message)[0];
          if (shouldDispatchAlert && canDisplayError && firstMessage) {
            toast.warning(Array.isArray(firstMessage) ? firstMessage[0] : firstMessage.toString(), {
              position: toast.POSITION.TOP_RIGHT,
              theme: "colored",
              autoClose: 6000,
            });
          }
        } else {
          // For other errors, map through all messages
          Object.values(message).map((item, key) => {
            // Send found messages to alert
            return (
              shouldDispatchAlert &&
              canDisplayError &&
              toast.warning(item.toString(), {
                position: toast.POSITION.TOP_RIGHT,
                theme: "colored",
                autoClose: 6000,
              })
            );
          });
        }
      } else if (message && typeof message === "string") {
        // Request made and server responded with an error

        handlerDump(
          "Error Response",
          errorObject?.response?.headers,
          errorObject?.response?.data,
          errorObject?.response?.status
        );

        // If error should not be displayed, log to console instead
        if (!canDisplayError) {
          console.error("Error Response:", {
            status: statusCode,
            message: message,
            headers: errorObject?.response?.headers,
            data: errorObject?.response?.data,
          });
        }

        // Send generic error message to alert
        shouldDispatchAlert &&
          canDisplayError &&
          toast.warning(message, {
            position: toast.POSITION.TOP_RIGHT,
            theme: "colored",
            autoClose: 6000,
          });

        // Return a response
        return handlerResponse("Something went wrong!", errorObject.response);
      } else {
        // Request made and server responded with an error
        handlerDump("Error Response", errorObject.response);

        // If error should not be displayed, log to console instead
        if (!canDisplayError) {
          console.error("Error Response:", {
            status: statusCode,
            response: errorObject.response,
          });
        }

        // Send generic error message to alert
        shouldDispatchAlert &&
          canDisplayError &&
          toast.warning("Something went wrong! Please try again", {
            position: toast.POSITION.TOP_RIGHT,
            theme: "colored",
            autoClose: 6000,
          });
      }
      // Return a response
      return handlerResponse("Something went wrong!", errorObject.response);
    } else if (errorObject.response.status === 413) {
      // 413 connection
      shouldDispatchAlert &&
        toast.error("File sizes are to large", {
          position: toast.POSITION.TOP_RIGHT,
          theme: "colored",
        });

      // Return a response
      return handlerResponse("Something went wrong!", errorObject);
    } else if (errorObject.request) {
      // The request was made but no response was received
      handlerDump("Unknown Response", errorObject.request);

      // No network connection
      if (errorObject.message === "Network Error") {
        // Send found errors to alert
        shouldDispatchAlert &&
          toast.error("Network is Unavailable", {
            position: toast.POSITION.TOP_RIGHT,
            theme: "colored",
          });
      } else {
        // Send generic error message to alert
        shouldDispatchAlert &&
          toast.error("Session has expired", {
            position: toast.POSITION.TOP_RIGHT,
            theme: "colored",
          });
      }

      // Return a response
      return handlerResponse("Something went wrong!", errorObject);
    } else {
      // Something happened in setting up the request that triggered an Error
      handlerDump("Unknown Error", errorObject);
      // Send generic info message to alert
      shouldDispatchAlert &&
        toast.error("Application is Unavailable", {
          position: toast.POSITION.TOP_RIGHT,
          theme: "colored",
        });

      // Return a response
      return handlerResponse("Something went wrong!", errorObject);
    }
  } catch (error) {
    handlerDump(
      "Error Handler System Failure - Error Experienced In Processing Error Object",
      error
    );
    handlerDump(
      "Error Handler System Failure - Error Object Passed In For Processing",
      errorObject
    );
  }
};

export default handler;
